import React, { useState } from 'react';
import moment from 'moment';
import NumberFormat from 'react-number-format';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon } from '@hugeicons/core-free-icons';
import { PaymentStatus } from '../../../../../shared/enums/payment-status.enum';
import { CURRENCY } from '../../../../../shared/enums/currency.enum';
import { PaymentModel } from '../../../../../shared/models/payment.model';
import { ReservationDetailsUiModel } from '../../../../../shared/ui-models/reservation.ui-model';
import {
  getServiceFeeStatus,
  ServiceFeeStatus
} from '../../../../../shared/services/helpers/service-fee.helper';
import { paymentTypeOptions } from '../../reservation-edit.helper';

export interface PaymentSummaryProps {
  data: {
    reservation: ReservationDetailsUiModel;
  };
}

const OPEN_STATUSES = [
  PaymentStatus.PENDING,
  PaymentStatus.OVERDUE,
  PaymentStatus.PARTIALLY_COLLECTED
];

const toEgp = (payment: PaymentModel, usdRate: number) => {
  const amount = payment.paymentAmount || 0;
  if (payment.direction === 'out') return -amount;
  return payment.currency === 'usd' ? amount * usdRate : amount;
};

const Money = ({ value, prefix = 'EGP ' }: { value: number; prefix?: string }) => (
  <NumberFormat
    value={value}
    displayType="text"
    thousandSeparator
    decimalScale={2}
    prefix={prefix}
  />
);

const currencyPrefix = (currency?: string) =>
  currency ? `${CURRENCY[currency as keyof typeof CURRENCY] || currency.toUpperCase()} ` : 'EGP ';

const Stat = ({ label, children }: { label: string; children: React.ReactNode }) => (
  <div className="min-w-0">
    <div className="text-[12px] text-neutral-300">{label}</div>
    <div className="mt-[2px] text-[14px] font-gilroy-medium text-neutral-500 truncate">
      {children}
    </div>
  </div>
);

const Dot = ({ className }: { className: string }) => (
  <span className={`inline-block size-[8px] rounded-full mr-[6px] align-middle ${className}`} />
);

export const PaymentSummary: React.FC<PaymentSummaryProps> = ({ data: { reservation } }) => {
  const [showFees, setShowFees] = useState(false);
  const usdRate = reservation.usdRate || 1;
  const payments = (reservation.paymentList || []).filter(
    (payment) => payment.status !== PaymentStatus.CANCELED
  );

  const total = payments.reduce((sum, payment) => sum + toEgp(payment, usdRate), 0);
  const collected = payments
    .filter((payment) => payment.status === PaymentStatus.COLLECTED)
    .reduce((sum, payment) => sum + toEgp(payment, usdRate), 0);
  const balance = Math.max(total - collected, 0);
  const overdue = payments
    .filter((payment) => payment.status === PaymentStatus.OVERDUE)
    .reduce((sum, payment) => sum + toEgp(payment, usdRate), 0);
  const isOverdue = overdue > 0;
  const progress = total > 0 ? Math.min(Math.max(collected / total, 0), 1) : 0;

  const nextDue = payments
    .filter((payment) => payment.direction !== 'out' && OPEN_STATUSES.includes(payment.status))
    .sort((a, b) => a.dueDate - b.dueDate)[0];
  const isNextDueLate = !!nextDue && moment(nextDue.dueDate).isBefore(moment(), 'day');
  const isSettled = !isOverdue && total > 0 && balance <= 0;

  const collectedPayments = payments.filter(
    (payment) => payment.status === PaymentStatus.COLLECTED
  );
  const collectedIn = collectedPayments.filter((payment) => payment.direction !== 'out');
  const refundsCount = collectedPayments.length - collectedIn.length;
  const usdCollected = collectedPayments
    .filter((payment) => payment.currency === 'usd')
    .reduce(
      (sum, payment) => sum + (payment.direction === 'out' ? -1 : 1) * (payment.paymentAmount || 0),
      0
    );
  const lastCollectedAt = collectedIn
    .map((payment) => payment.collectedAt)
    .filter(Boolean)
    .sort()
    .pop();

  const methodPayment = collectedIn[0] || payments.find((payment) => payment.direction !== 'out');
  const methodType = reservation.paymentMethod || methodPayment?.paymentType;
  const methodLabel = methodType
    ? paymentTypeOptions.find((option) => option.value === methodType)?.label || methodType
    : undefined;
  const gatewayLabel =
    !reservation.paymentMethod && methodPayment?.paymentGateway
      ? methodPayment.paymentGateway.charAt(0).toUpperCase() + methodPayment.paymentGateway.slice(1)
      : undefined;

  const badge = isOverdue
    ? { label: 'Overdue', className: 'bg-secondary-50 text-secondary-700' }
    : total > 0 && balance <= 0
    ? { label: 'Fully paid', className: 'bg-primary-50 text-primary-700' }
    : collected > 0
    ? { label: 'Partially paid', className: 'bg-creams-300 text-neutral-500' }
    : { label: 'Not paid', className: 'bg-creams-300 text-neutral-500' };

  const rate = reservation.rateCard?.[0];
  const hasRateChange = !!rate?.basePrice && !!rate?.price && rate.basePrice !== rate.price;
  const serviceFee = getServiceFeeStatus(reservation.paymentList);
  const currency = reservation.paymentList?.[0]?.currency;
  const adjustments = [
    { label: 'Refund', value: reservation.refund },
    { label: 'Compensation', value: reservation.compensation },
    { label: 'Insurance collected', value: reservation.insuranceCollected }
  ].filter((item) => item.value && item.value.amount > 0);

  return (
    <div className="rounded-[12px] bg-creams-100 px-[20px] py-[14px]">
      <div className="flex flex-wrap items-center gap-x-[32px] gap-y-[12px]">
        <div className="min-w-[220px]">
          <div className="flex items-center gap-[8px]">
            <span className="text-[18px] leading-tight font-gilroy-bold text-neutral-500">
              <Money value={collected} />
            </span>
            <span
              className={`px-[8px] py-[2px] rounded-full text-[11px] font-gilroy-medium whitespace-nowrap ${badge.className}`}
            >
              {badge.label}
            </span>
          </div>
          <div className="mt-[6px] flex items-center gap-[8px]">
            <div className="w-[96px] h-[4px] rounded-full overflow-hidden bg-creams-300">
              <div
                className={`h-full ${isOverdue ? 'bg-scarlet' : 'bg-primary-500'}`}
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            <span className="text-[12px] text-neutral-300 whitespace-nowrap">
              {isSettled ? (
                usdCollected > 0 ? (
                  <>
                    ≈ <Money value={usdCollected} prefix="$" /> · USD booking
                  </>
                ) : (
                  'Total received'
                )
              ) : (
                <>
                  of <Money value={total} />
                </>
              )}
            </span>
          </div>
        </div>

        <div className="hidden md:block h-[32px] w-px bg-gray-200" />

        <div className="flex flex-wrap items-center gap-x-[32px] gap-y-[12px]">
          <Stat label="Payment">
            {isOverdue ? (
              <span className="text-scarlet">
                <Dot className="bg-scarlet" />
                Overdue
              </span>
            ) : (
              <span className="text-primary-700">
                <Dot className="bg-primary-500" />
                On time
              </span>
            )}
          </Stat>
          {isSettled ? (
            <>
              <Stat label="Last payment">
                {lastCollectedAt ? moment(lastCollectedAt).format('MMM D, YYYY') : '—'}
              </Stat>
              <Stat label="Payments">
                {collectedIn.length} collected
                {refundsCount > 0 &&
                  ` · ${refundsCount} ${refundsCount === 1 ? 'refund' : 'refunds'}`}
              </Stat>
            </>
          ) : (
            <>
              <Stat label={isOverdue ? 'Overdue' : 'Balance due'}>
                <span className={isOverdue ? 'text-scarlet' : ''}>
                  <Money value={isOverdue ? overdue : balance} />
                </span>
              </Stat>
              <Stat label="Next due">
                {nextDue ? (
                  <span className={isNextDueLate ? 'text-scarlet' : ''}>
                    <Money
                      value={nextDue.paymentAmount || 0}
                      prefix={currencyPrefix(nextDue.currency)}
                    />
                    {' · '}
                    {moment(nextDue.dueDate).format('MMM D')}
                  </span>
                ) : (
                  '—'
                )}
              </Stat>
            </>
          )}
          <Stat label="Method">
            {methodLabel ? (
              [methodLabel, gatewayLabel].filter(Boolean).join(' · ')
            ) : (
              <span className={collected > 0 ? 'text-neutral-300' : 'text-scarlet'}>Not set</span>
            )}
          </Stat>
        </div>

        <button
          type="button"
          onClick={() => setShowFees((open) => !open)}
          aria-expanded={showFees}
          className="ml-auto inline-flex items-center gap-[6px] text-[13px] text-neutral-300 hover:text-neutral-500 cursor-pointer"
        >
          Rate & fees
          <HugeiconsIcon
            icon={ArrowDown01Icon}
            size={14}
            className={`transition-transform ${showFees ? 'rotate-180' : ''}`}
          />
        </button>
      </div>

      {showFees && (
        <div className="mt-[12px] pt-[12px] border-t border-gray-200 flex flex-wrap gap-x-[32px] gap-y-[12px]">
          {currency && <Stat label="Currency">{currency.toUpperCase()}</Stat>}
          {rate?.price ? (
            <Stat label="Rate">
              {rate.price}
              {hasRateChange && (
                <span className="ml-[6px] text-[13px] text-neutral-300 line-through">
                  {rate.basePrice}
                </span>
              )}
            </Stat>
          ) : null}
          {reservation.channelCommission?.amount ? (
            <Stat label="Channel commission">
              <Money
                value={reservation.channelCommission.amount}
                prefix={currencyPrefix(reservation.channelCommission.currency)}
              />
            </Stat>
          ) : null}
          {serviceFee && (
            <Stat label="Service fee">
              {serviceFee === ServiceFeeStatus.WAIVED ? 'Waived' : 'Included'}
            </Stat>
          )}
          {adjustments.map((item) => (
            <Stat key={item.label} label={item.label}>
              <Money value={item.value!.amount} prefix={currencyPrefix(item.value!.currency)} />
            </Stat>
          ))}
        </div>
      )}
    </div>
  );
};
